import { MetricBlock, MetricCard, MetricStatus } from "entities/assets";

import { AirQualityCardData, AirQualityMetric } from "modules/assets/AirQualityCard";
import { TagTone } from "modules/assets/Tag";

import { AIR_QUALITY_CARDS } from "./data";

type Pill = { tone: TagTone; label: string };
type MetricTone = "green" | "orange" | "red" | "gray";

const STATUS_LABEL: Record<MetricStatus, string> = { good: "Good", warning: "Warning", alert: "Alert" };
const STATUS_TONE: Record<MetricStatus, MetricTone> = { good: "green", warning: "orange", alert: "red" };
const STATUS_TAG_TONE: Record<MetricStatus, TagTone> = { good: "green", warning: "orange", alert: "red" };

const OVERALL_PILL: Record<MetricStatus | "no_data", Pill> = {
  good: { tone: "green", label: "Good" },
  warning: { tone: "orange", label: "Warning" },
  alert: { tone: "red", label: "Alert" },
  no_data: { tone: "gray", label: "No data" },
};

const TLV_C_TAG: Pill = { tone: "violet", label: "TLV-C" };
const ST_TAG: Pill = { tone: "blue", label: "ST" };
const LT_TAG: Pill = { tone: "green", label: "LT" };

const blockTone = (status: MetricStatus | null): MetricTone => (status ? STATUS_TONE[status] : "gray");

const blockBadge = (status: MetricStatus | null): Pill | undefined =>
  status ? { tone: STATUS_TAG_TONE[status], label: STATUS_LABEL[status] } : undefined;

const fmt = (value: number | null, unit: string): string => (value === null ? "No data" : `${value} ${unit}`);

const fmtDelta = (value: number | null, unit: string): string => {
  if (value === null) return "No data";
  if (value > 0) return `+${value} ${unit}`;
  if (value < 0) return `${value} ${unit}`;
  return `0 ${unit}`;
};

const limitBlock = (
  block: MetricBlock,
  label: string,
  tag: Pill,
  unit: string,
  info?: string,
): AirQualityMetric => ({
  tone: blockTone(block.status),
  value: fmt(block.value, unit),
  badge: blockBadge(block.status),
  info,
  label,
  tag,
  progress: block.fill ?? undefined,
  limit: block.limit === null ? undefined : `Limit: ${block.limit} ${unit}`,
});

const shortTermBlock = (block: MetricBlock, label: string, unit: string, info?: string): AirQualityMetric => {
  if (block.outdoor !== null) {
    return {
      tone: blockTone(block.status),
      value: fmtDelta(block.value, unit),
      badge: blockBadge(block.status),
      info,
      label,
      tag: ST_TAG,
      subInputs: [
        { label: "Outdoor", value: `${block.outdoor}`, unit },
        {
          label: "Indoor",
          value: block.indoor === null ? "No data" : `${block.indoor}`,
          unit: block.indoor === null ? undefined : unit,
        },
      ],
      delta: {
        label: "Delta (above outdoor)",
        value: fmtDelta(block.value, unit),
        unit: block.value === null ? undefined : unit,
      },
      progress: block.fill ?? undefined,
      limit: block.limit === null ? undefined : `Limit: +${block.limit} ${unit}`,
    };
  }

  return {
    tone: blockTone(block.status),
    value: fmt(block.value, unit),
    badge: blockBadge(block.status),
    info,
    label,
    tag: ST_TAG,
    progress: block.fill ?? undefined,
    limit: block.limit === null ? undefined : `Limit: ${block.limit} ${unit}`,
  };
};

const pad = (n: number): string => String(n).padStart(2, "0");

export const formatLastUpdate = (iso: string | null): string => {
  if (!iso) return "Last update: never";
  const then = new Date(iso).getTime();
  const diffMin = Math.floor((Date.now() - then) / 60000);
  if (diffMin < 1) return "Last update: Just now";
  if (diffMin < 60) return `Last update: ${diffMin} min ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `Last update: ${diffH} h ago`;
  const gst = new Date(then + 4 * 60 * 60 * 1000);
  const date = `${pad(gst.getUTCDate())}.${pad(gst.getUTCMonth() + 1)}.${gst.getUTCFullYear()}`;
  const time = `${pad(gst.getUTCHours())}:${pad(gst.getUTCMinutes())}`;
  return `Last update: ${date} ${time} (GST)`;
};

const NO_INSTANT_CEILING = "No instant TLV-C per DM-HSD-GU119";

const ESTIMATED_INFO =
  "This value is estimated. The sensor reports a TVOC index (scale 0–5), which has been converted to a " +
  "concentration value (µg/m³) using the conversion factor provided by the device manufacturer. " +
  "The result is an approximation and may differ from a direct concentration measurement.";

const undefinedInstantBlock = (block: MetricBlock, unit: string): AirQualityMetric => ({
  tone: "gray",
  value: fmt(block.value, unit),
  label: "Instant ceiling",
  tag: TLV_C_TAG,
  limit: NO_INSTANT_CEILING,
});

export type BuildOptions = { shortTermLabel: string; longTermLabel?: string; approximateInfo?: string };

export type MetricSlug = "co2" | "hcho" | "tvoc" | "pm25" | "pm10";

export const METRIC_BUILD_OPTIONS: Record<MetricSlug, BuildOptions> = {
  co2: { shortTermLabel: "Short-term - vs outdoor level" },
  hcho: { shortTermLabel: "Short-term: 30-min average" },
  tvoc: { shortTermLabel: "Short-term: 30-min average", approximateInfo: "Approximate conversion (MOX sensor)" },
  pm25: { shortTermLabel: "Short-term: 1-hour average", longTermLabel: "Long-term: 24-hour average" },
  pm10: { shortTermLabel: "Short-term: 8-hour average", longTermLabel: "Long-term: 24-hour average" },
};

const METRIC_SLUG_FORMULA: Record<MetricSlug, string> = {
  co2: "CO₂",
  hcho: "HCHO",
  tvoc: "TVOC",
  pm25: "PM2.5",
  pm10: "PM10",
};

export const isMetricSlug = (slug: string): slug is MetricSlug => slug in METRIC_BUILD_OPTIONS;

export const buildMetricCard = (
  base: AirQualityCardData,
  card: MetricCard,
  options: BuildOptions,
): AirQualityCardData => ({
  ...base,
  unitInfo: card.estimated ? ESTIMATED_INFO : undefined,
  status: OVERALL_PILL[card.overall_status],
  realtimeValue: card.real_time === null ? "No data" : `${card.real_time}`,
  realtimeUnit: card.real_time === null ? "" : card.unit,
  metrics: [
    card.instant_defined
      ? limitBlock(card.instant, "Instant ceiling", TLV_C_TAG, card.unit, options.approximateInfo)
      : undefinedInstantBlock(card.instant, card.unit),
    shortTermBlock(card.short_term, options.shortTermLabel, card.unit, options.approximateInfo),
    limitBlock(card.long_term, options.longTermLabel ?? "Long-term: 8-hour average", LT_TAG, card.unit),
  ],
  footerNote: card.sensor_count > 1 ? "Click to see per-sensor breakdown" : undefined,
  updatedNote: formatLastUpdate(card.last_update),
  outdatedNote: card.data_may_be_outdated ? "Data may be outdated" : undefined,
});

export const buildMetricCardBySlug = (slug: string, card: MetricCard): AirQualityCardData | null => {
  if (!isMetricSlug(slug)) return null;
  const base = AIR_QUALITY_CARDS.find((entry) => entry.formula === METRIC_SLUG_FORMULA[slug]);
  if (!base) return null;
  return buildMetricCard(base, card, METRIC_BUILD_OPTIONS[slug]);
};
